import React, { useState } from 'react';
import { Input, InputNumber, Table } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { AwFormSelect } from './ArcWeldingListShell';
import {
  AW_METHODS,
  AW_FORM_LABELS,
  AW_PARAMETER_LABELS,
  AW_TEST_POINT_COLUMNS,
  AW_TEST_POINT_COLUMNS_SHORT,
  AW_VALUE_CLASSIFICATIONS,
  getPointResultStyle,
  methodForClassification,
} from '../../utils/arcWeldingVerificationUi';

const buildDefaultPoints = (count = 7) =>
  Array.from({ length: count }, (_, i) => ({
    pointNo: i + 1,
    machineSettingOrDisplay: null,
    firstTestReferenceValue: null,
    todayMeterReading: null,
    absError: null,
    result: null,
  }));

const ArcWeldingTestPointBlock = ({
  parameter,
  valueClassification,
  method,
  permittedTolerance,
  sectionResult,
  points = [],
  readOnly,
  valueClassifications = AW_VALUE_CLASSIFICATIONS,
  onClassificationChange,
  onPointChange,
  defaultCollapsed = true,
}) => {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const effectiveMethod = method || methodForClassification(valueClassification);
  const isNotTested = effectiveMethod === AW_METHODS.NOT_TESTED;

  const handlePointField = (pointNo, field, value) => {
    const base = points.length > 0 ? points : buildDefaultPoints();
    const next = base.map((p) => {
      if (p.pointNo !== pointNo) return p;
      const updated = { ...p, [field]: value };
      if (field === 'machineSettingOrDisplay') {
        updated.machineDisplayValue = value;
        updated.setOrControlValue = value;
      }
      return updated;
    });
    onPointChange?.(next);
  };

  const displayPoints = points.length > 0 ? points : buildDefaultPoints();
  const settingValue = (row) => row.machineSettingOrDisplay ?? row.machineDisplayValue ?? row.setOrControlValue;
  const displaySectionResult = sectionResult || (isNotTested ? 'Not tested' : 'Incomplete');
  const firstPoint = displayPoints[0];
  const firstReading = firstPoint?.todayMeterReading;
  const firstSetting = settingValue(firstPoint);

  const columns = [{
    title: AW_TEST_POINT_COLUMNS_SHORT.point,
    dataIndex: 'pointNo',
    width: 44,
    align: 'center',
  }];

  if (effectiveMethod === AW_METHODS.VALIDATION) {
    columns.push({
      title: AW_TEST_POINT_COLUMNS_SHORT.setControl,
      key: 'machineSettingOrDisplay',
      width: 110,
      render: (_, row) =>
        readOnly ? (
          settingValue(row) ?? '—'
        ) : (
          <InputNumber
            className="aw-cell-entry"
            style={{ width: '100%' }}
            value={settingValue(row)}
            onChange={(v) => handlePointField(row.pointNo, 'machineSettingOrDisplay', v)}
          />
        ),
    });
  } else if (effectiveMethod === AW_METHODS.CONSISTENCY) {
    columns.push(
      {
        title: AW_TEST_POINT_COLUMNS_SHORT.controlPosition,
        key: 'machineSettingOrDisplay',
        width: 100,
        render: (_, row) =>
          readOnly ? (
            settingValue(row) ?? '—'
          ) : (
            <InputNumber
              className="aw-cell-entry"
              style={{ width: '100%' }}
              value={settingValue(row)}
              onChange={(v) => handlePointField(row.pointNo, 'machineSettingOrDisplay', v)}
            />
          ),
      },
      {
        title: AW_TEST_POINT_COLUMNS_SHORT.firstTestReference,
        key: 'firstTestReferenceValue',
        width: 90,
        render: (_, row) =>
          readOnly ? (
            row.firstTestReferenceValue ?? '—'
          ) : (
            <InputNumber
              className="aw-cell-entry"
              style={{ width: '100%' }}
              value={row.firstTestReferenceValue}
              onChange={(v) => handlePointField(row.pointNo, 'firstTestReferenceValue', v)}
            />
          ),
      }
    );
  } else if (effectiveMethod === AW_METHODS.CALIBRATION) {
    columns.push({
      title: AW_TEST_POINT_COLUMNS_SHORT.machineDisplay,
      key: 'machineSettingOrDisplay',
      width: 110,
      render: (_, row) =>
        readOnly ? (
          settingValue(row) ?? '—'
        ) : (
          <InputNumber
            className="aw-cell-entry"
            style={{ width: '100%' }}
            value={settingValue(row)}
            onChange={(v) => handlePointField(row.pointNo, 'machineSettingOrDisplay', v)}
          />
        ),
    });
  }

  if (!isNotTested) {
    columns.push(
      {
        title: AW_TEST_POINT_COLUMNS_SHORT.todayMeterReading,
        key: 'todayMeterReading',
        width: 100,
        render: (_, row) =>
          readOnly ? (
            row.todayMeterReading ?? '—'
          ) : (
            <InputNumber
              className="aw-cell-entry"
              style={{ width: '100%' }}
              value={row.todayMeterReading}
              onChange={(v) => handlePointField(row.pointNo, 'todayMeterReading', v)}
            />
          ),
      },
      {
        title: AW_TEST_POINT_COLUMNS_SHORT.absError,
        key: 'absError',
        width: 72,
        align: 'center',
        render: (_, row) => (
          <span className="aw-cell-auto-badge" style={getPointResultStyle()}>
            {row.absError ?? '—'}
          </span>
        ),
      },
      {
        title: AW_TEST_POINT_COLUMNS_SHORT.result,
        key: 'result',
        width: 72,
        align: 'center',
        render: (_, row) => (
          <span className="aw-cell-auto-badge" style={getPointResultStyle(row.result)}>
            {row.result ?? '—'}
          </span>
        ),
      }
    );
  }

  return (
    <div className={`aw-section aw-test-point-block${expanded ? ' aw-test-point-block--expanded' : ' aw-test-point-block--collapsed'}`}>
      <button
        type="button"
        className="aw-test-point-block-header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="aw-test-point-block-header-main">
          <div className="aw-section-title aw-test-point-block-title">
            {AW_PARAMETER_LABELS[parameter] ?? parameter}
          </div>
          {!expanded && (
            <div className="aw-test-point-collapsed-summary">
              <span className="aw-test-point-summary-item">
                <span className="aw-test-point-summary-label">{AW_FORM_LABELS.valueClassification}</span>
                <span className="aw-test-point-summary-value">{valueClassification || '—'}</span>
              </span>
              <span className="aw-test-point-summary-item">
                <span className="aw-test-point-summary-label">{AW_FORM_LABELS.method}</span>
                <span className="aw-test-point-summary-value">{effectiveMethod}</span>
              </span>
              <span className="aw-test-point-summary-item">
                <span className="aw-test-point-summary-label">{AW_FORM_LABELS.permittedTolerance}</span>
                <span className="aw-test-point-summary-value">{permittedTolerance || '—'}</span>
              </span>
              {!isNotTested && (firstSetting != null && firstSetting !== '') && (
                <span className="aw-test-point-summary-item">
                  <span className="aw-test-point-summary-label">Pt 1</span>
                  <span className="aw-test-point-summary-value">{firstSetting}</span>
                </span>
              )}
              {!isNotTested && (firstReading != null && firstReading !== '') && (
                <span className="aw-test-point-summary-item">
                  <span className="aw-test-point-summary-label">{AW_TEST_POINT_COLUMNS.todayMeterReading}</span>
                  <span className="aw-test-point-summary-value">{firstReading}</span>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="aw-test-point-block-header-end">
          <span className="aw-test-point-result-pill" style={getPointResultStyle(displaySectionResult)}>
            {displaySectionResult}
          </span>
          {expanded ? <UpOutlined /> : <DownOutlined />}
        </div>
      </button>

      {expanded && (
        <>
      <div className="aw-test-point-meta">
        <div className="aw-test-point-meta-item aw-test-point-meta-item--classification">
          <div className="aw-test-point-meta-label">{AW_FORM_LABELS.valueClassification}</div>
          {readOnly ? (
            <span>{valueClassification || '—'}</span>
          ) : (
            <AwFormSelect
              className="aw-classification-select"
              options={valueClassifications}
              placeholder="Select Classification"
              allowClear
              showSearch
              value={valueClassification || undefined}
              onChange={onClassificationChange}
            />
          )}
        </div>
        <div className="aw-test-point-meta-item">
          <div className="aw-test-point-meta-label">{AW_FORM_LABELS.method}</div>
          <Input
            className="aw-cell-auto-field"
            disabled
            value={effectiveMethod}
          />
        </div>
        <div className="aw-test-point-meta-item aw-test-point-meta-item--tolerance">
          <div className="aw-test-point-meta-label">{AW_FORM_LABELS.permittedTolerance}</div>
          <Input
            className="aw-cell-auto-field"
            disabled
            value={permittedTolerance ?? ''}
            placeholder="—"
          />
        </div>
        {sectionResult != null && sectionResult !== '' && (
          <div className="aw-test-point-meta-item">
            <div className="aw-test-point-meta-label">{AW_FORM_LABELS.sectionResult}</div>
            <Input
              className="aw-cell-auto-field"
              disabled
              value={sectionResult}
              style={getPointResultStyle(sectionResult)}
            />
          </div>
        )}
        {!isNotTested && (!sectionResult || sectionResult === '') && (
          <div className="aw-test-point-meta-item">
            <div className="aw-test-point-meta-label">{AW_FORM_LABELS.sectionResult}</div>
            <Input
              className="aw-cell-auto-field"
              disabled
              value="Incomplete"
              style={getPointResultStyle('Incomplete')}
            />
          </div>
        )}
      </div>
      {!isNotTested && (
        <div className="aw-test-point-table-wrap">
          <Table
            size="small"
            rowKey="pointNo"
            pagination={false}
            columns={columns}
            dataSource={displayPoints}
            className="aw-test-point-table"
            tableLayout="fixed"
            scroll={{ x: 'max-content' }}
          />
        </div>
      )}
      {isNotTested && (
        <p className="aw-test-point-not-tested">Not Tested — Record Reason in Remarks.</p>
      )}
        </>
      )}
    </div>
  );
};

export default ArcWeldingTestPointBlock;
